<template>
  <div class="reset-password-page" dir="rtl">
    <div class="logo-container">
      <img
        src="@/assets/image/3 - With text.png"
        alt="أكاديمية الدكتور"
        class="logo"
      />
    </div>

    <div class="reset-password-form">
      <!-- صفحة النجاح -->
      <div v-if="isSuccess" class="success-message">
        <div class="success-icon">✓</div>
        <h2 class="success-title">تم بنجاح!</h2>
        <p class="success-text">تم إعادة تعيين كلمة المرور بنجاح</p>
        <p class="success-subtext">يمكنك الآن تسجيل الدخول باستخدام كلمة المرور الجديدة</p>
        <router-link to="/login" class="btn btn-success">
          الذهاب إلى تسجيل الدخول
        </router-link>
      </div>

      <!-- نموذج إعادة تعيين كلمة المرور -->
      <template v-else>
        <h2 class="form-title">إعادة تعيين كلمة المرور</h2>
        
        <div v-if="!isValidToken" class="error-message">
          <p>⚠️ الرابط غير صالح أو منتهي الصلاحية</p>
          <p>يرجى طلب رابط جديد لإعادة تعيين كلمة المرور</p>
        </div>

        <form v-else @submit.prevent="handleResetPassword">
        <div class="form-group">
          <label for="email" class="form-label">البريد الإلكتروني</label>
          <input
            type="email"
            class="form-control"
            id="email"
            v-model="resetData.email"
            placeholder="أدخل البريد الإلكتروني"
            required
            :disabled="loading"
          />
        </div>

        <div class="form-group">
          <label for="newPassword" class="form-label">كلمة المرور الجديدة</label>
          <input
            type="password"
            class="form-control"
            id="newPassword"
            v-model="resetData.newPassword"
            placeholder="أدخل كلمة المرور الجديدة"
            required
            :disabled="loading"
            minlength="6"
          />
        </div>

        <div class="form-group">
          <label for="confirmPassword" class="form-label">تأكيد كلمة المرور</label>
          <input
            type="password"
            class="form-control"
            id="confirmPassword"
            v-model="resetData.confirmPassword"
            placeholder="أعد إدخال كلمة المرور الجديدة"
            required
            :disabled="loading"
            minlength="6"
          />
        </div>

        <div v-if="passwordMismatch" class="error-message">
          <p>⚠️ كلمة المرور وتأكيد كلمة المرور غير متطابقين</p>
        </div>

        <button 
          type="submit" 
          class="btn btn-primary"
          :disabled="loading || passwordMismatch"
        >
          <span v-if="loading">جاري المعالجة...</span>
          <span v-else>إعادة تعيين كلمة المرور</span>
        </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import apiClient from "@/api";
import Swal from "sweetalert2";

export default {
  name: "ResetPasswordPage",
  data() {
    return {
      resetData: {
        email: "",
        token: "",
        newPassword: "",
        confirmPassword: "",
      },
      loading: false,
      isValidToken: true,
      isSuccess: false,
    };
  },
  computed: {
    passwordMismatch() {
      return (
        this.resetData.newPassword &&
        this.resetData.confirmPassword &&
        this.resetData.newPassword !== this.resetData.confirmPassword
      );
    },
  },
  mounted() {
    // قراءة token و email من query parameters
    const token = this.$route.query.token;
    const email = this.$route.query.email;

    if (token && email) {
      this.resetData.token = token;
      this.resetData.email = decodeURIComponent(email);
    } else {
      // إذا لم يكن هناك token أو email، الرابط غير صالح
      this.isValidToken = false;
      Swal.fire({
        icon: "error",
        title: "رابط غير صالح",
        text: "الرابط غير صالح أو منتهي الصلاحية. يرجى طلب رابط جديد.",
        confirmButtonText: "حسنًا",
      }).then(() => {
        this.$router.push("/login");
      });
    }
  },
  methods: {
    async handleResetPassword() {
      // التحقق من تطابق كلمة المرور
      if (this.passwordMismatch) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "كلمة المرور وتأكيد كلمة المرور غير متطابقين",
          confirmButtonText: "حسنًا",
        });
        return;
      }

      // التحقق من طول كلمة المرور
      if (this.resetData.newPassword.length < 6) {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
          confirmButtonText: "حسنًا",
        });
        return;
      }

      this.loading = true;

      try {
        const response = await apiClient.post(
          "/api/Auth/ResetPassword",
          {
            email: this.resetData.email,
            token: this.resetData.token,
            newPassword: this.resetData.newPassword,
            confirmPassword: this.resetData.confirmPassword,
          }
        );

        if (response.status === 200 && !response.data.error) {
          // عرض صفحة النجاح
          this.isSuccess = true;
        } else {
          Swal.fire({
            icon: "error",
            title: "خطأ",
            text:
              response.data.message ||
              "فشل إعادة تعيين كلمة المرور، يرجى المحاولة مرة أخرى",
            confirmButtonText: "حسنًا",
          });
        }
      } catch (error) {
        console.error("خطأ أثناء إعادة تعيين كلمة المرور:", error);
        
        let errorMessage = "فشل إعادة تعيين كلمة المرور، يرجى المحاولة مرة أخرى";
        
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage;
        }

        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: errorMessage,
          confirmButtonText: "حسنًا",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* إعدادات عامة للصفحة */
.reset-password-page {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f0f4f8, #d9e2ec);
  padding: 20px;
}

/* صندوق الشعار */
.logo-container {
  margin-bottom: 30px;
  text-align: center;
}

/* تعديل حجم الشعار ليناسب الهوية */
.logo {
  width: 300px;
  height: auto;
}

/* تصميم نموذج إعادة تعيين كلمة المرور */
.reset-password-form {
  background-color: #ffffff;
  padding: 40px 30px;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
}

/* عنوان النموذج */
.form-title {
  margin-bottom: 20px;
  color: #002f49;
  text-align: center;
  font-size: 24px;
  font-weight: 400;
}

/* تنسيق حقول النموذج */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  color: #002f49;
  font-weight: 600;
}

.form-control {
  direction: rtl;
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ccd0d5;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.form-control:focus {
  border-color: #002f49;
  outline: none;
  box-shadow: 0 0 5px rgba(0, 47, 73, 0.2);
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

/* رسالة الخطأ */
.error-message {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  color: #856404;
  text-align: center;
}

.error-message p {
  margin: 5px 0;
}

/* زر إعادة تعيين كلمة المرور */
.btn-primary {
  width: 100%;
  background-color: #002f49;
  border: none;
  padding: 12px;
  font-size: 16px;
  color: #ffffff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 10px;
}

.btn-primary:hover:not(:disabled) {
  background-color: #001f33;
}

.btn-primary:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

/* صفحة النجاح */
.success-message {
  text-align: center;
  padding: 20px 0;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  background-color: #28a745;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  color: #ffffff;
  font-weight: bold;
}

.success-title {
  color: #28a745;
  font-size: 28px;
  margin-bottom: 15px;
  font-weight: 600;
}

.success-text {
  color: #002f49;
  font-size: 18px;
  margin-bottom: 10px;
  font-weight: 500;
}

.success-subtext {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 30px;
}

.btn-success {
  width: 100%;
  background-color: #28a745;
  border: none;
  padding: 12px;
  font-size: 16px;
  color: #ffffff;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
  text-decoration: none;
  display: inline-block;
}

.btn-success:hover {
  background-color: #218838;
}

/* تحسينات للشاشات الصغيرة */
@media (max-width: 480px) {
  .reset-password-form {
    padding: 30px 20px;
  }

  .logo {
    width: 250px;
  }

  .form-title {
    font-size: 20px;
  }
}
</style>



